import { Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";

import countries from "src/config/countries";
import nigStates from "src/config/nigStates";
import { register } from "src/api/auth";
import { getBusinessOrganizationTypes } from "src/api";
import AuthField from "src/components/auth/AuthField";
import AuthHeading from "src/components/auth/AuthHeading";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { cn, showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("First name is required"),
	last_name: Yup.string().required("Last name is required"),
	email: Yup.string().email("Invalid email address").required("Email is required"),
	phone: Yup.string().required("Phone is required"),
	business_name: Yup.string().required("Business name is required"),
	business_address: Yup.string().required("Business address is required"),
	organization_type_id: Yup.string().required("Business type is required"),
	country: Yup.string().required("Country is required"),
	state: Yup.string().required("State is required"),
	password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
	password_confirmation: Yup.string()
		.oneOf([Yup.ref("password")], "Passwords must match")
		.required("Confirm password is required"),
});

const initialValues = {
	first_name: "",
	last_name: "",
	email: "",
	phone: "",
	business_name: "",
	business_address: "",
	organization_type_id: "",
	country: "",
	state: "",
	password: "",
	password_confirmation: "",
};

const SelectField = ({
	label,
	name,
	value,
	options,
	onChange,
	error,
	placeholder,
}: {
	label: string;
	name: string;
	value: string;
	options: { value: string; label: string }[];
	onChange: (value: string) => void;
	error?: string | false;
	placeholder: string;
}) => (
	<div className="tw-space-y-1.5">
		<Label htmlFor={name}>{label}</Label>
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger
				id={name}
				aria-invalid={Boolean(error)}
				className={cn("tw-h-11 tw-rounded-[10px] !tw-border-border tw-text-[14px]", error && "!tw-border-red")}
			>
				<SelectValue placeholder={placeholder} />
			</SelectTrigger>
			<SelectContent>
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
		{error && <p className="tw-text-[12.5px] tw-text-red">{error}</p>}
	</div>
);

const Signup = () => {
	const navigate = useNavigate();

	const { data: rawOrganizationTypes } = useQuery({
		queryKey: ["business-organization-types"],
		queryFn: () => getBusinessOrganizationTypes(),
		suspense: true,
	});

	const organizationTypes = rawOrganizationTypes?.data ?? [];

	const { mutate, isLoading } = useMutation(register, {
		onSuccess: ({ message }, { email }) => {
			showSuccessToast(message || "Account created");
			navigate("/otp-verification", { state: { email } });
		},
	});

	return (
		<>
			<AuthHeading title="Create an account" subtitle="Complete the sign-up process to get started." />

			<Formik
				validateOnBlur={false}
				validateOnMount={false}
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={(values) => mutate(values)}
			>
				{({ values, handleChange, handleSubmit, errors, touched, setFieldValue }) => (
					<form onSubmit={handleSubmit} className="tw-space-y-4">
						<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
							<AuthField
								label="First Name"
								name="first_name"
								placeholder="First name"
								value={values.first_name}
								onChange={handleChange}
								error={touched.first_name && errors.first_name}
							/>
							<AuthField
								label="Last Name"
								name="last_name"
								placeholder="Last name"
								value={values.last_name}
								onChange={handleChange}
								error={touched.last_name && errors.last_name}
							/>
						</div>

						<AuthField
							label="Email"
							name="email"
							type="email"
							placeholder="you@business.com"
							value={values.email}
							onChange={handleChange}
							error={touched.email && errors.email}
							autoComplete="email"
						/>

						<AuthField
							label="Phone"
							name="phone"
							placeholder="Phone number"
							value={values.phone}
							onChange={handleChange}
							error={touched.phone && errors.phone}
							autoComplete="tel"
						/>

						<AuthField
							label="Business Name"
							name="business_name"
							placeholder="Business name"
							value={values.business_name}
							onChange={handleChange}
							error={touched.business_name && errors.business_name}
						/>

						<AuthField
							label="Business Address"
							name="business_address"
							placeholder="Business address"
							value={values.business_address}
							onChange={handleChange}
							error={touched.business_address && errors.business_address}
						/>

						<SelectField
							label="Business Type"
							name="organization_type_id"
							placeholder="Select business type"
							value={values.organization_type_id}
							options={organizationTypes.map((type) => ({ value: String(type.id), label: type.name }))}
							onChange={(value) => setFieldValue("organization_type_id", value)}
							error={touched.organization_type_id && errors.organization_type_id}
						/>

						<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
							<SelectField
								label="Country"
								name="country"
								placeholder="Select country"
								value={values.country}
								options={countries.map((country: { name: string }) => ({ value: country.name, label: country.name }))}
								onChange={(value) => setFieldValue("country", value)}
								error={touched.country && errors.country}
							/>
							<SelectField
								label="State"
								name="state"
								placeholder="Select state"
								value={values.state}
								options={nigStates.map((state: { name: string }) => ({ value: state.name, label: state.name }))}
								onChange={(value) => setFieldValue("state", value)}
								error={touched.state && errors.state}
							/>
						</div>

						<AuthField
							label="Password"
							name="password"
							isPassword
							placeholder="At least 8 characters"
							value={values.password}
							onChange={handleChange}
							error={touched.password && errors.password}
							autoComplete="new-password"
						/>

						<AuthField
							label="Confirm Password"
							name="password_confirmation"
							isPassword
							placeholder="Re-enter your password"
							value={values.password_confirmation}
							onChange={handleChange}
							error={touched.password_confirmation && errors.password_confirmation}
							autoComplete="new-password"
						/>

						<Button type="submit" disabled={isLoading} className="tw-w-full tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
							{isLoading ? "Creating..." : "Create Account"}
						</Button>

						<p className="tw-text-center tw-text-[13px] tw-text-text-soft">
							Already signed up?{" "}
							<Link to="/login" className="tw-font-semibold tw-text-teal hover:tw-underline">
								Log in
							</Link>
						</p>

						<p className="tw-text-center tw-text-[12px] tw-text-text-soft">
							By clicking <span className="tw-font-semibold tw-text-teal">Create Account</span>, you agree to Paylinq Solutions'
							Terms of Service and Privacy Policy.
						</p>
					</form>
				)}
			</Formik>
		</>
	);
};

export default Signup;
